import { useId, useRef, useState } from 'react';
import type { UploadInput } from '../api';
import {
  ACCEPTED_FILE_TYPES, STUDENT_RECORDS_NOTICE, clientRefusal, defaultTitle, uploadRefusalCopy,
} from '../upload-copy';

/**
 * Putting a file in front of the group (R11, R15).
 *
 * The student-records sentence is visible text beside the file input — not a tooltip, not a
 * collapsed panel, and not something a teacher has to hover to find. That is the Key
 * Decision, and the structural half of it is that the schema has no column a student record
 * could live in. There is no detector and there should not be one.
 *
 * The refusal copy (`upload-copy.ts`) is worded *separately* from that sentence on purpose.
 * "This file was refused" and "do not upload student work" are two different messages, and a
 * teacher who sees them merged reads the second as an accusation about the file they chose.
 *
 * **The form empties itself when a share succeeds, and only then.** `onUpload` answers
 * whether the Worker took the file, and that answer is the whole of the reset rule: a refusal
 * leaves everything exactly as the teacher left it — the copy for `network` says so in as many
 * words — and a success clears the title, the note and the file input together. It used to
 * clear none of them, which left the form primed to share the same file a second time, one
 * click away, with the title still in it. The `<input type="file">` needs `.value = ''` as
 * well as state: its value is the browser's, not React's, and a controlled file input is not
 * a thing that exists.
 *
 * This form has no heading of its own. It is the whole of its own page now
 * (`MaterialUploadPage`), and that page's `<h2>` is the heading — a second one here would put
 * two titles on one screen and an `h3` under no `h2` in the outline.
 */

// The copy moved to `upload-copy.ts` so the upload panel reads the same sentences. Re-exported
// here because the room and the tests already import the notice from this file.
export { STUDENT_RECORDS_NOTICE, UPLOAD_REFUSAL_COPY, uploadRefusalCopy } from '../upload-copy';

export interface MaterialUploadProps {
  busy: boolean;
  /** The Worker's `reason`, or `network` for a connection that dropped. */
  refusal: string | null;
  /**
   * Share it. Resolves `true` when the Worker took the file — the signal this form empties
   * itself on, and the reason the parent cannot forget to tell it.
   */
  onUpload: (input: UploadInput) => Promise<boolean>;
  /** A client-side refusal, so the same vocabulary reaches analytics from both sides. */
  onRejected?: (reason: string) => void;
  /** The way out without sharing anything. */
  onCancel?: () => void;
  /**
   * Several files at once. Present only where an upload queue is mounted: the picker then
   * takes `multiple`, and a choice of more than one file is handed over whole — the title and
   * note fields describe one file, so they step aside and each file's row in the upload panel
   * carries its own title.
   */
  onMany?: (files: File[]) => void;
}

export default function MaterialUpload({
  busy, refusal, onUpload, onRejected, onCancel, onMany,
}: MaterialUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [local, setLocal] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const fileId = useId();
  const titleId = useId();
  const noteId = useId();

  /** One file: the cheap half of `decideUpload` (`clientRefusal`), before any bytes move. */
  function choose(next: File | null) {
    setLocal(null);
    if (!next) { setFile(null); return; }

    const reason = clientRefusal(next);

    if (reason) {
      setFile(null);
      setLocal(uploadRefusalCopy(reason));
      onRejected?.(reason);
      if (input.current) input.current.value = '';
      return;
    }

    setFile(next);
    if (title.trim().length === 0) setTitle(defaultTitle(next.name));
  }

  /** Everything the teacher chose, gone — the file input included, which state cannot clear. */
  function clear() {
    setFile(null);
    setTitle('');
    setNote('');
    setLocal(null);
    if (input.current) input.current.value = '';
  }

  async function submit() {
    if (!file || busy) return;
    if (title.trim().length === 0) { setLocal(uploadRefusalCopy('invalid_title')); return; }
    const shared = await onUpload({ file, title: title.trim(), note });
    // Only on a success. A refusal leaves the teacher's file and words where they were, which
    // is what the `network` copy promises in as many words.
    if (shared) clear();
  }

  const message = local ?? uploadRefusalCopy(refusal);

  return (
    <form
      className="panel upload-form"
      onSubmit={(event) => { event.preventDefault(); void submit(); }}
    >
      <label htmlFor={titleId}>Title</label>
      <input
        id={titleId}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder="What is this?"
        disabled={busy}
      />

      <label htmlFor={noteId}>Note (optional)</label>
      <textarea
        id={noteId}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        rows={2}
        placeholder="What would you like a second opinion on?"
        disabled={busy}
      />

      <label htmlFor={fileId}>File</label>
      <input
        id={fileId}
        ref={input}
        type="file"
        disabled={busy}
        accept={ACCEPTED_FILE_TYPES}
        multiple={onMany !== undefined}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (onMany && files.length > 1) {
            if (input.current) input.current.value = '';
            onMany(files);
            return;
          }
          choose(files[0] ?? null);
        }}
      />

      {/*
        * R15, at the point of upload, in the open. The Key Decision is explicit that this is
        * visible text next to the file input — never a tooltip and never behind a disclosure,
        * and never smaller than the copy around it.
        */}
      <p className="notice">{STUDENT_RECORDS_NOTICE}</p>

      {message && <p className="error" role="alert">{message}</p>}

      {busy && <p className="hint" role="status">Uploading…</p>}

      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy || file === null}>
          {busy ? 'Sharing…' : 'Share with the group'}
        </button>
        {onCancel && (
          <button type="button" className="secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
